
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { marked } from 'marked';
import DOMPurify from "dompurify";

@Component
export default class MarkdownViewer extends Vue {
  @Prop({ type: String, default: "" }) readonly content!: string;
  @Prop({ type: Boolean, default: true }) readonly flat!: boolean;
  @Prop({ type: Boolean, default: true }) readonly outlined!: boolean;
  @Prop({ type: Boolean, default: false }) readonly transparent!: boolean;
  @Prop({ type: Number, default: 0 }) readonly elevation!: number;
  @Prop({ type: Object, default: () => ({}) }) readonly options!: Record<
    string,
    any
  >;

  renderedContent = "";

  // Optionen für marked
  markedOptions = {
    breaks: true, // Zeilenumbrüche aktivieren
    gfm: true, // GitHub Flavored Markdown
    headerIds: true, // Header-IDs für Ankerlinks
    sanitize: false, // Wir verwenden DOMPurify zur Bereinigung
    ...this.options,
  };

  mounted() {
    this.renderMarkdown();
  }

  @Watch("content")
  onContentChange() {
    this.renderMarkdown();
  }

  renderMarkdown(): void {
    if (!this.content) {
      this.renderedContent = "";
      return;
    }

    // Use Marked explicit synchron, to make sure to get a string and not a promise
    try {
      marked.setOptions(this.markedOptions);

      // preprocessing for special Inline-Syntax
      const preprocessedContent = this.preProcessSpecialSyntax(this.content);

      let rawHtml = marked(preprocessedContent, { async: false }) as string;

      // console.log("Original Content:", this.content);
      // console.log("Preprocessed Content:", preprocessedContent);
      // console.log("Raw HTML:", rawHtml);

      // process additional markdown syntax e.g. ::: hljs-center
      let processedHtml = this.processSpecialSyntax(rawHtml);

      // Replace escaped <br> with a placeholder so DOMPurify won't re-escape it
      const BR_PLACEHOLDER = '%%MDVIEWER_BR%%';
      processedHtml = processedHtml.replace(/&lt;br\s*\/?>/gi, BR_PLACEHOLDER);

      // cleanup HTML
      const sanitized = DOMPurify.sanitize(processedHtml);

      // restore real <br/> tags from placeholder after sanitization
      let restored = sanitized.replace(new RegExp(BR_PLACEHOLDER, 'g'), '<br/>');

      // Also convert possible escaped entity forms that remained (e.g. &lt;br&gt; or double-encoded &amp;lt;br&amp;gt;)
      restored = restored
        .replace(/&amp;lt;br\s*\/?&amp;gt;/gi, '<br/>')
        .replace(/&lt;br\s*\/?&gt;/gi, '<br/>');

      this.renderedContent = restored;
    } catch (error) {
      console.error('Fehler beim Parsen von Markdown:', error);
      this.renderedContent = this.content; // Fallback to original text
    }
  }

  preProcessSpecialSyntax(markdown: string): string {
    let processedMarkdown = markdown;

    // Process < and > and convert to HTML entities to avoid issues with HTML tags,
    // but only outside of code blocks (inline and fenced).
    // Preserve leading '>' blockquote markers at the start of lines.
    const escapeAngleBrackets = (text: string): string => {
      return text
        .split('\n')
        .map(line => {
          // Keep blockquote prefix (up to 3 spaces + one or more '>' chars), escape the rest
          const blockquote = line.match(/^([ \t]{0,3}>+)(.*)/);
          if (blockquote) {
            return blockquote[1] + blockquote[2].replace(/</g, '&lt;').replace(/>/g, '&gt;');
          }
          return line.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        })
        .join('\n');
    };

    let segments: string[] = [];
    let regex = /(```[\s\S]*?```|`[^`]*`)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(processedMarkdown)) !== null) {
      // Before the code block: escape and replace emojis
      segments.push(this.replaceEmojis(escapeAngleBrackets(processedMarkdown.substring(lastIndex, match.index))));
      // Code block itself: unchanged
      segments.push(match[0]);
      lastIndex = regex.lastIndex;
    }

    // Remainder after the last code block (also replace emojis there)
    segments.push(this.replaceEmojis(escapeAngleBrackets(processedMarkdown.substring(lastIndex))));
    processedMarkdown = segments.join('');

    // Process underline (++Text++)
    // Only if there is no whitespace or line break directly after ++ and before ++
    // Start: ++Word, End: Word++
    processedMarkdown = processedMarkdown.replace(
      /\+\+([^\s][^+\n\r]*?[^\s])\+\+/g,
      (match, content) => {
        return `<underline>${content}</underline>`;
      }
    );

    // Process highlighting (==Text==)
    // Only if there is no whitespace or line break directly after == and before ==
    // Start: ==Word, End: Word==
    processedMarkdown = processedMarkdown.replace(
      /==([^\s][^=\n\r]*?[^\s])==/g,
      (match, content) => {
        return `<mark>${content}</mark>`;
      }
    );

    // Process superscript (Test^2^)
    processedMarkdown = processedMarkdown.replace(/\^([\s\S]*?)\^/g, (match, content) => {
      return `<sup>${content}</sup>`;
    });

    // Process subscript (Test~2~)
    processedMarkdown = processedMarkdown.replace(/~([\s\S]*?)~/g, (match, content) => {
      return `<sub>${content}</sub>`;
    });

    return processedMarkdown;
  }

  // Replace :shortcode: and simple emoticons with Unicode emoji
  replaceEmojis(markdown: string): string {
    if (!markdown) return markdown;

    const emojiMap: Record<string, string> = {
      laughing: '😆',
      blush: '😊',
      smiley: '😃',
      smile: '😄',
      wink: '😉',
      heart: '❤️',
      grinning: '😀',
      joy: '😂',
      cry: '😢',
      angry: '😠',
      thumbs_up: '👍',
      thumbsup: '👍',
      tada: '🎉',
      confetti_ball: '🎊',
      // add more mappings as needed
    };

    // Replace :shortcode: (only when mapping exists)
    markdown = markdown.replace(/:([a-z0-9_\+\-]+):/gi, (match, code) => {
      const key = code.toLowerCase();
      return emojiMap[key] || match;
    });

    // Simple emoticon replacements
    const emoticonMap: Record<string, string> = {
      ':-)': '🙂',
      ':)': '🙂',
      ':-(': '☹️',
      ':(': '☹️',
      ':-D': '😄',
      ':D': '😄',
      ':-P': '😜',
      ':P': '😜',
      ';-)': '😉',
      ';)': '😉'
    };

    const escapeForRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    for (const [k, v] of Object.entries(emoticonMap)) {
      const re = new RegExp(escapeForRegex(k), 'g');
      markdown = markdown.replace(re, v);
    }

    return markdown;
  }

  processSpecialSyntax(html: string): string {
    // Verarbeite die Block-Formatierungen
    let processedHtml = html
      .replace(
        /<p>:::\s*hljs-center\s*<\/p>([\s\S]*?)<p>:::\s*<\/p>/g,
        '<div class="text-center">$1</div>'
      )
      .replace(
        /<p>:::\s*hljs-right\s*<\/p>([\s\S]*?)<p>:::\s*<\/p>/g,
        '<div class="text-right">$1</div>'
      )
      .replace(
        /<p>:::\s*hljs-left\s*<\/p>([\s\S]*?)<p>:::\s*<\/p>/g,
        '<div class="text-left">$1</div>'
      );

    // Ersetze unsere speziellen Tags durch die entsprechenden HTML-Tags
    processedHtml = processedHtml
      .replace(/<underline>([\s\S]*?)<\/underline>/g, '<u>$1</u>')
      .replace(/<strikethrough>([\s\S]*?)<\/strikethrough>/g, '<s>$1</s>');

    return processedHtml;
  }

  get contentStyle() {
    if (this.outlined)
      return " padding-left: 16px; padding-right: 16px;";

      return " padding-left: 0px; padding-right: 0px;";
  }

  get backgroundColor() {
    if (this.transparent)
      return "transparent";
    return "";
  }

}
